import React from 'react';
import { 
  Box, 
  Typography, 
  TextField, 
  FormControl, 
  Select, 
  MenuItem, 
  Switch, 
  FormControlLabel,
  Button,
  IconButton,
  Tooltip,
  alpha,
  Menu
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import SaveIcon from '@mui/icons-material/Save';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RestoreIcon from '@mui/icons-material/Restore';
import { useTranslation } from 'react-i18next';

/**
 * DocumentDetailsBar Component
 * Displays document date, VAT rate, items count, and price type toggle
 * Reusable across Delivery Notes, Sale Orders, and Invoices
 */
const DocumentDetailsBar = ({
  documentDate,
  onDateChange,
  vatPercentage,
  onVatChange,
  vatRates = [],
  itemsCount = 0,
  isTvaIncluded,
  onTvaToggle,
  dateLabel,
  showPriceTypeToggle = true,
  disableDateEdit = false,
  showProfit = false,
  onProfitToggle,
  saleOrderNumber,
  documentNumber,
  documentNumberLabel,
  status,
  saleOrderId,
  onSaveDraft,
  onCloseOrder,
  onReopenOrder,
  disabled = false,
  disableSaveDraft = false,
  dueDate,
  onDueDateChange,
  dueDateLabel,
  useDateTime = false,
}) => {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSaveDraft = () => {
    handleClose();
    if (onSaveDraft) onSaveDraft();
  };

  const handleCloseOrder = () => {
    handleClose();
    if (onCloseOrder) onCloseOrder();
  };

  const handleReopenOrder = () => {
    handleClose();
    if (onReopenOrder) onReopenOrder();
  };

  return (
    <Box sx={{
      display: 'flex',
      gap: 3,
      mb: 4,
      p: 3,
      background: '#f8f9fa',
      borderRadius: '8px',
      borderLeft: '4px solid #667eea',
      flexWrap: 'wrap',
      boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
    }}>
      {/* Document Number (generic) */}
      {(documentNumber || saleOrderNumber) && (
        <Box sx={{ flex: 1, minWidth: '180px' }}>
          <Typography 
            variant="caption" 
            fontWeight="600" 
            color="#2c3e50" 
            sx={{ textTransform: 'uppercase', display: 'block', mb: 0.5, fontSize: '0.75rem' }}
          >
            {documentNumber ? (documentNumberLabel || t("document_number") || "NUMBER") : (t("sale_order") || "SALE ORDER")}
          </Typography>
          <Typography 
            variant="h6" 
            fontWeight="700" 
            sx={{ 
              color: '#667eea',
              letterSpacing: '0.5px',
              mt: 0.5
            }}
          >
            {documentNumber || saleOrderNumber}
          </Typography>
        </Box>
      )}
      
      {/* Date Field */}
      <Box sx={{ flex: 1, minWidth: '150px' }}>
        <Typography 
          variant="caption" 
          fontWeight="600" 
          color="#2c3e50" 
          sx={{ textTransform: 'uppercase', display: 'block', mb: 0.5, fontSize: '0.75rem' }}
        >
          {dateLabel || t("document_date")}
        </Typography>
        <TextField
          type={useDateTime ? "datetime-local" : "date"}
          value={documentDate || ''}
          onChange={(e) => onDateChange(useDateTime ? e.target.value : new Date(e.target.value))}
          size="small"
          fullWidth
          disabled={disableDateEdit}
          InputLabelProps={{ shrink: true }}
          sx={{
            '& .MuiOutlinedInput-root': {
              bgcolor: 'white',
              '& fieldset': { borderColor: '#e0e0e0' }
            }
          }}
        />
      </Box>

      {/* Due Date Field */}
      {onDueDateChange && (
        <Box sx={{ flex: 1, minWidth: '150px' }}>
          <Typography 
            variant="caption" 
            fontWeight="600" 
            color="#2c3e50" 
            sx={{ textTransform: 'uppercase', display: 'block', mb: 0.5, fontSize: '0.75rem' }}
          >
            {dueDateLabel || t("due_date") || "Due Date"}
          </Typography>
          <TextField
            type="date"
            value={dueDate || ''}
            onChange={(e) => onDueDateChange(e.target.value)}
            size="small"
            fullWidth
            disabled={disabled}
            sx={{
              '& .MuiOutlinedInput-root': {
                bgcolor: 'white',
                '& fieldset': { borderColor: '#e0e0e0' }
              }
            }}
          />
        </Box>
      )}

      {/* VAT Rate */}
      <Box sx={{ flex: 1, minWidth: '120px' }}>
        <Typography 
          variant="caption" 
          fontWeight="600" 
          color="#2c3e50" 
          sx={{ textTransform: 'uppercase', display: 'block', mb: 0.5, fontSize: '0.75rem' }}
        >
          {t("vat_rate")}
        </Typography>
        <FormControl fullWidth size="small">
          <Select
            value={vatPercentage}
            onChange={(e) => onVatChange(parseFloat(e.target.value))}
            disabled={disabled}
            sx={{
              bgcolor: 'white',
              '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e0e0e0' }
            }}
          >
            {vatRates.length > 0 ? (
              vatRates.map((rate) => (
                <MenuItem key={rate.Id} value={rate.VATPercentage}>
                  {rate.VATPercentage}%
                </MenuItem>
              ))
            ) : (
              <>
                <MenuItem value={0}>0%</MenuItem>
                <MenuItem value={6}>6%</MenuItem>
                <MenuItem value={12}>12%</MenuItem>
                <MenuItem value={21}>21%</MenuItem>
              </>
            )}
          </Select>
        </FormControl>
      </Box>

      {/* Items Count */}
      <Box sx={{ flex: 1, minWidth: '120px' }}>
        <Typography 
          variant="caption" 
          fontWeight="600" 
          color="#2c3e50" 
          sx={{ textTransform: 'uppercase', display: 'block', mb: 0.5, fontSize: '0.75rem' }}
        >
          {t("items")}
        </Typography>
        <Typography variant="h6" fontWeight="600" color="#667eea" sx={{ mt: 0.5 }}>
          {itemsCount}
        </Typography>
      </Box>

      {/* Price Type Toggle */}
      {showPriceTypeToggle && (
        <Box sx={{ flex: 1, minWidth: '140px' }}>
          <Typography 
            variant="caption" 
            fontWeight="600" 
            color="#2c3e50" 
            sx={{ textTransform: 'uppercase', display: 'block', mb: 0.5, fontSize: '0.75rem' }}
          >
            {t("price_type") || "Price Type"}
          </Typography>
          <FormControlLabel
            control={
              <Switch
                checked={isTvaIncluded}
                onChange={(e) => onTvaToggle(e.target.checked)}
                disabled={disabled}
                size="small"
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': {
                    color: '#667eea',
                  },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                    backgroundColor: '#667eea',
                  },
                }}
              />
            }
            label={
              <Typography variant="caption" sx={{ fontWeight: 600, color: '#2c3e50' }}>
                {t("tva_included") || "TVA Incl"}
              </Typography>
            }
            sx={{ mt: 0.5 }}
          />
        </Box>
      )}

      {/* Actions Dropdown */}
      {saleOrderId && (
        <Box sx={{ flex: 1, minWidth: '150px' }}>
          <Typography 
            variant="caption" 
            fontWeight="600" 
            color="#2c3e50" 
            sx={{ textTransform: 'uppercase', display: 'block', mb: 0.5, fontSize: '0.75rem' }}
          >
            {t("actions") || "ACTIONS"}
          </Typography>
          {status === 'Draft' ? (
            <>
              <Button
                variant="contained"
                size="small"
                onClick={handleClick}
                endIcon={<ArrowDropDownIcon />}
                fullWidth
                sx={{
                  bgcolor: '#667eea',
                  '&:hover': { bgcolor: '#764ba2' },
                  fontWeight: 600,
                  boxShadow: '0 2px 8px rgba(102, 126, 234, 0.3)',
                  textTransform: 'none',
                  mt: 0.5
                }}
              >
                {t('actions') || 'Actions'}
              </Button>
              <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                PaperProps={{
                  sx: {
                    mt: 1,
                    minWidth: 200,
                    boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                    borderRadius: '8px'
                  }
                }}
              >
                {onProfitToggle && (
                  <MenuItem 
                    onClick={() => {
                      onProfitToggle(!showProfit);
                      handleClose();
                    }}
                    sx={{
                      py: 1.5,
                      px: 2,
                      '&:hover': { bgcolor: alpha('#2196f3', 0.1) }
                    }}
                  >
                    {showProfit ? (
                      <VisibilityOffIcon sx={{ mr: 1.5, fontSize: 20, color: '#2196f3' }} />
                    ) : (
                      <VisibilityIcon sx={{ mr: 1.5, fontSize: 20, color: '#2196f3' }} />
                    )}
                    <Typography variant="body2" fontWeight={500}>
                      {showProfit ? (t('hide_cost_prices') || 'Hide Cost Prices') : (t('show_cost_prices') || 'Show Cost Prices')}
                    </Typography>
                  </MenuItem>
                )}
                <MenuItem 
                  onClick={handleSaveDraft}
                  disabled={disabled || disableSaveDraft}
                  sx={{
                    py: 1.5,
                    px: 2,
                    '&:hover': { bgcolor: alpha('#ff9800', 0.1) }
                  }}
                >
                  <SaveIcon sx={{ mr: 1.5, fontSize: 20, color: '#ff9800' }} />
                  <Typography variant="body2" fontWeight={500}>
                    {t('save_as_draft') || 'Save as Draft'}
                  </Typography>
                </MenuItem>
                <MenuItem 
                  onClick={handleCloseOrder}
                  sx={{
                    py: 1.5,
                    px: 2,
                    '&:hover': { bgcolor: alpha('#4caf50', 0.1) }
                  }}
                >
                  <CheckCircleIcon sx={{ mr: 1.5, fontSize: 20, color: '#4caf50' }} />
                  <Typography variant="body2" fontWeight={500}>
                    {t('close_order') || 'Close Order'}
                  </Typography>
                </MenuItem>
              </Menu>
            </>
          ) : (
            <Button
              variant="contained"
              size="small"
              onClick={handleReopenOrder}
              startIcon={<RestoreIcon />}
              fullWidth
              sx={{
                bgcolor: '#2196f3',
                '&:hover': { bgcolor: '#1976d2' },
                fontWeight: 600,
                boxShadow: '0 2px 8px rgba(33, 150, 243, 0.3)',
                textTransform: 'none',
                mt: 0.5
              }}
            >
              {t('reopen_order') || 'Reopen Order'}
            </Button>
          )}
        </Box>
      )}
    </Box>
  );
};

export default DocumentDetailsBar;
